import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

/* ── Gallery ── */

export interface GalleryItem {
  id: string;
  appId: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  voteCount: number;
  hasVoted: boolean;
  author: { displayName: string; avatarUrl: string | null };
  createdAt: string;
}

interface GalleryResponse {
  items: GalleryItem[];
  total: number;
  hasMore: boolean;
}

export function useGallery(app: string = 'all') {
  const { apiFetch, isAuthenticated } = useApi();
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchGallery = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiFetch<GalleryResponse>(
        `/api/gallery?app=${app}&sort=recent&limit=20`,
      );
      setItems(res.items);
    } catch {
      // keep existing items on error
    } finally {
      setIsLoading(false);
    }
  }, [apiFetch, app]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const toggleVote = useCallback(async (submissionId: string) => {
    if (!isAuthenticated) return;
    // Optimistic update
    setItems(prev => prev.map(item =>
      item.id === submissionId
        ? { ...item, hasVoted: !item.hasVoted, voteCount: item.hasVoted ? item.voteCount - 1 : item.voteCount + 1 }
        : item
    ));
    try {
      const res = await apiFetch<{ voteCount: number; hasVoted: boolean }>(
        '/api/gallery/vote',
        { method: 'POST', body: JSON.stringify({ submissionId }) },
      );
      // Reconcile with server
      setItems(prev => prev.map(item =>
        item.id === submissionId
          ? { ...item, voteCount: res.voteCount, hasVoted: res.hasVoted }
          : item
      ));
    } catch {
      // Revert on error
      setItems(prev => prev.map(item =>
        item.id === submissionId
          ? { ...item, hasVoted: !item.hasVoted, voteCount: item.hasVoted ? item.voteCount - 1 : item.voteCount + 1 }
          : item
      ));
    }
  }, [apiFetch, isAuthenticated]);

  return { items, isLoading, toggleVote, refresh: fetchGallery };
}

/* ── Build Log ── */

export interface BuildEntry {
  id: string;
  title: string;
  description: string;
  type: 'release' | 'dev' | 'planned';
  appId: string;
  version?: string;
  createdAt: string;
}

export function useBuildLog() {
  const { apiFetch } = useApi();
  const [entries, setEntries] = useState<BuildEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch<{ entries: BuildEntry[] }>('/api/build-log?limit=20');
        if (!cancelled) setEntries(res.entries);
      } catch {
        // keep empty
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiFetch]);

  return { entries, isLoading };
}

/* ── Features ── */

export interface Feature {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  voteCount: number;
  hasVoted: boolean;
  createdAt: string;
}

export function useFeatures() {
  const { apiFetch, isAuthenticated } = useApi();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchFeatures = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiFetch<{ features: Feature[]; total: number }>('/api/features?limit=30');
      setFeatures(res.features);
    } catch {
      // keep existing
    } finally {
      setIsLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  const toggleVote = useCallback(async (featureId: string) => {
    if (!isAuthenticated) return;
    // Optimistic update
    setFeatures(prev => prev.map(f =>
      f.id === featureId
        ? { ...f, hasVoted: !f.hasVoted, voteCount: f.hasVoted ? f.voteCount - 1 : f.voteCount + 1 }
        : f
    ));
    try {
      const res = await apiFetch<{ voteCount: number; hasVoted: boolean }>(
        '/api/features/vote',
        { method: 'POST', body: JSON.stringify({ featureId }) },
      );
      setFeatures(prev => prev.map(f =>
        f.id === featureId
          ? { ...f, voteCount: res.voteCount, hasVoted: res.hasVoted }
          : f
      ));
    } catch {
      setFeatures(prev => prev.map(f =>
        f.id === featureId
          ? { ...f, hasVoted: !f.hasVoted, voteCount: f.hasVoted ? f.voteCount - 1 : f.voteCount + 1 }
          : f
      ));
    }
  }, [apiFetch, isAuthenticated]);

  return { features, isLoading, toggleVote, refresh: fetchFeatures };
}
